import { ValidationResult } from './types'
import { isString, isValidCardinalData, isValidSetupCompleteData } from './cardinalTypeGuards'
import { SecurePaymentsParesStatus } from '../../../api/types'

let isSetupComplete = false
let initializationPromise: Promise<string> | null = null

export function setupPaymentValidation(): Promise<ValidationResult> {
  // Remove existing listeners to prevent duplicates
  return new Promise((resolve, reject) => {
    Cardinal.off('payments.validated')
    // Set up validation event listener
    Cardinal.on('payments.validated', (data: any, jwt?: string) => {
      // Validate the response structure
      if (!isValidCardinalData(data)) {
        reject(new Error('Invalid Cardinal response structure'))
        return
      }

      if (!jwt || !isString(jwt)) {
        reject(new Error('Invalid JWT token'))
        return
      }

      // Handle all possible ActionCode values
      switch (data.ActionCode) {
        case 'SUCCESS':
        case 'NOACTION':
          // SUCCESS = authentication succeeded, NOACTION = frictionless flow (no challenge required)
          resolve({ validated: true, data, jwt })
          break

        case 'FAILURE':
        case 'ERROR':
        case 'TIMEOUT':
        case 'CANCEL':
        default:
          // Handle all error cases
          reject({
            validated: false,
            data,
            jwt,
            message: data.ErrorDescription || `Cardinal validation failed: ${data.ActionCode}`,
          })
          break
      }
    })
  })
}

export function initializeCardinal(threeDSecureToken: string, language: string = 'is-IS'): Promise<string> {
  if (isSetupComplete) {
    // If already initialized, we might need to return a stored session ID or just resolve immediately
    if (initializationPromise) {
      return initializationPromise
    }
  }

  initializationPromise = new Promise((resolve, reject) => {
    try {
      // Remove existing listeners to prevent duplicates
      Cardinal.off('payments.setupComplete')
      Cardinal.off('cca')
      Cardinal.configure({
        timeout: '6000',
        maxRequestRetries: 3,
        logging: {
          level: process.env.NODE_ENV === 'production' ? 'on' : 'verbose',
        },
        payment: {
          view: 'modal',
          framework: 'modal',
          displayLoading: true,
        },
        i18n: {
          default_language: language,
        },
      })
      // Wait for setup to complete
      Cardinal.on('payments.setupComplete', (setupCompleteData: any) => {
        if (setupCompleteData?.ActionCode === 'ERROR') {
          isSetupComplete = false
          initializationPromise = null
          reject(new Error(setupCompleteData?.ErrorDescription || 'Cardinal setup failed'))
          return
        }

        // Validate the response structure
        if (!isValidSetupCompleteData(setupCompleteData)) {
          isSetupComplete = false
          initializationPromise = null
          reject(new Error('Invalid Cardinal setup response structure'))
          return
        }

        isSetupComplete = true
        resolve(setupCompleteData.sessionId)
      })

      // Setup Cardinal with JWT token
      Cardinal.setup('init', {
        jwt: threeDSecureToken,
      })
    } catch (error) {
      isSetupComplete = false
      initializationPromise = null
      reject(error)
    }
  })

  return initializationPromise
}

/**
 * Trigger 3D Secure authentication using data from Verifone's 3DS Lookup API
 * @param apiLookupResponse Response from Verifone's 3DS Lookup API
 * @returns Promise that resolves when authentication completes
 */
export function triggerCardinalAuthenticationWithVerifone(apiLookupResponse: {
  paresStatus: SecurePaymentsParesStatus
  accessControlServerUrl: string
  payload: string
  transactionId: string
}): Promise<any> {
  return new Promise((resolve, reject) => {
    if (!isSetupComplete) {
      reject(new Error('Cardinal setup not complete'))
      return
    }
    // Cases where we can resolve immediately
    if (
      apiLookupResponse.paresStatus === SecurePaymentsParesStatus.SUCCESSFUL_AUTHENTICATION ||
      apiLookupResponse.paresStatus === SecurePaymentsParesStatus.SUCCESSFUL_ATTEMPTS_TRANSACTION
    ) {
      resolve({
        authenticated: true,
        challengeRequired: false,
        apiLookupResponse,
      })
      return
    }

    if (
      apiLookupResponse.paresStatus === SecurePaymentsParesStatus.FAILED_AUTHENTICATION ||
      apiLookupResponse.paresStatus === SecurePaymentsParesStatus.REJECTED_AUTHENTICATION
    ) {
      reject(new Error('3D Secure authentication failed with status: ' + apiLookupResponse.paresStatus))
      return
    }

    if (apiLookupResponse.paresStatus === SecurePaymentsParesStatus.CHALLENGE_REQUIRED_FOR_AUTHENTICATION) {
      try {
        const continuePayload = {
          AcsUrl: apiLookupResponse.accessControlServerUrl,
          Payload: apiLookupResponse.payload,
        }
        const orderDetails = {
          OrderDetails: {
            TransactionId: apiLookupResponse.transactionId,
          },
        }

        // Call the continue function to trigger the challenge
        Cardinal.continue('cca', continuePayload, orderDetails)
      } catch (error) {
        reject(error)
      }
    } else {
      reject(new Error('3D Secure authentication failed with status: ' + apiLookupResponse.paresStatus))
    }
  })
}
